import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Card,
  CardContent,
  Button,
  alpha,
  useTheme,
  IconButton,
  Skeleton,
  Dialog,
  Avatar,
} from "@mui/material";
import {
  Search,
  ShoppingBag,
  ArrowBack,
  InfoOutlined,
  Close,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAllProducts, orderRequest } from "../redux/slices/product/thunk";
import CustomTextField from "../components/RTLTextField";

const BASE_URL = "https://red-90.com";
const joinUrl = (path) =>
  path?.startsWith("http") ? path : `${BASE_URL}${path}`;

export default function ProductsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const dispatch = useDispatch();

  const { products, isLoading } = useSelector((state) => state.product);
  const primaryMain = theme.palette.primary.main;

  // States
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    // جلب جميع المنتجات بدون pagination (limit كبير)
    dispatch(getAllProducts({ query: "page=1&limit=2000" }));
  }, [dispatch]);

  // استخراج التصنيفات الفريدة ديناميكياً
  const categories = useMemo(() => {
    const cats =
      products?.data?.map((p) => (isRtl ? p.category_ar : p.category_en)) || [];
    return ["All", ...new Set(cats)];
  }, [products, isRtl]);

  // منطق الفلترة الأمامي
  const filteredProducts = useMemo(() => {
    return products?.data?.filter((product) => {
      const title = isRtl ? product.title_ar : product.title_en;
      const matchesSearch = title
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

      const categoryValue = isRtl ? product.category_ar : product.category_en;
      const matchesCategory =
        selectedCategory === "All" || categoryValue === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory, isRtl]);

  const handleOrderRequest = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setOrderDialogOpen(true);
  };

  const confirmOrder = () => {
    // منطق إرسال الطلب (مثال: dispatch لأكشن طلب المنتج)
    const data = {
      product: selectedProduct._id,
      quantity: String(quantity),
    };
    dispatch(orderRequest({ data }));

    // إغلاق النافذة بعد التأكيد
    setOrderDialogOpen(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 10 }}>
      {/* 1. Header & Search Section */}
      <Box
        sx={{
          bgcolor: "background.paper",
          pt: 4,
          pb: 4,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: "sticky",
          top: 0,
          zIndex: 10,
          backdropFilter: "blur(10px)",
          backgroundColor: alpha(theme.palette.background.paper, 0.9),
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ bgcolor: alpha(primaryMain, 0.05) }}
            >
              <ArrowBack
                sx={{ transform: isRtl ? "rotate(180deg)" : "none" }}
              />
            </IconButton>
            <Typography variant="h4" fontWeight={1000} sx={{ flexGrow: 1 }}>
              {t("home.products.title")}
            </Typography>
          </Stack>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <CustomTextField
                isRtl={isRtl}
                fullWidth
                placeholder={isRtl ? "ابحث عن منتج..." : "Search products..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                  sx: {
                    borderRadius: 4,
                    bgcolor: "background.default",
                    height: 55,
                  },
                }}
              />
            </Grid>
          </Grid>

          {/* Categories Quick Filter */}
          {/* <Stack direction="row" spacing={1} sx={{ mt: 3, overflowX: "auto", py: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat === "All" ? (isRtl ? "الكل" : "All") : cat}
                onClick={() => setSelectedCategory(cat)}
                variant={selectedCategory === cat ? "contained" : "outlined"}
                color={selectedCategory === cat ? "primary" : "default"}
                sx={{ 
                  fontWeight: 800, px: 2, height: 40, borderRadius: 3,
                  transition: '0.3s',
                  '&:hover': { transform: 'translateY(-2px)' }
                }}
              />
            ))}
          </Stack> */}
        </Container>
      </Box>

      {/* 2. Products Grid */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          {isLoading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton
                  variant="rectangular"
                  height={350}
                  sx={{ borderRadius: 5 }}
                />
              </Grid>
            ))
          ) : filteredProducts?.length > 0 ? (
            filteredProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} key={product._id}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 5,
                    overflow: "hidden",
                    border: `1px solid ${theme.palette.divider}`,
                    transition: "0.4s",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: `0 20px 40px ${alpha(primaryMain, 0.12)}`,
                      borderColor: primaryMain,
                    },
                  }}
                >
                  {/* Product Image Area */}
                  <Box
                    sx={{
                      height: 260,
                      p: 3,
                      bgcolor: alpha(primaryMain, 0.02),
                      position: "relative",
                      textAlign: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={joinUrl(product.image)}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                    <Chip
                      label={isRtl ? product.category_ar : product.category_en}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 15,
                        right: 15,
                        fontWeight: 900,
                        bgcolor: "background.paper",
                        boxShadow: 1,
                      }}
                    />
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 1000,
                        mb: 1,
                        height: 50,
                        overflow: "hidden",
                        lineHeight: 1.2,
                      }}
                    >
                      {isRtl ? product.title_ar : product.title_en}
                    </Typography>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-end"
                      sx={{ mt: 2 }}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={800}
                        >
                          {isRtl ? "السعر" : "Price"}
                        </Typography>
                        <Typography
                          variant="h5"
                          color="primary"
                          fontWeight={1000}
                        >
                          {new Intl.NumberFormat(i18n.language).format(
                            product.price,
                          )}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 0.5, fontWeight: 700 }}
                          >
                            EGP
                          </Typography>
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => navigate(`/product/${product._id}`)}
                        sx={{
                          bgcolor: alpha(primaryMain, 0.08),
                          color: primaryMain,
                        }}
                      >
                        <InfoOutlined />
                      </IconButton>
                    </Stack>

                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ShoppingBag />}
                      onClick={() => handleOrderRequest(product)}
                      sx={{
                        mt: 3,
                        borderRadius: 4,
                        py: 1.6,
                        fontWeight: 900,
                        fontSize: "0.95rem",
                      }}
                    >
                      {t("home.products.order_now")}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))
          ) : (
            <Box sx={{ width: "100%", textAlign: "center", py: 15 }}>
              <Typography variant="h5" fontWeight={800} color="text.secondary">
                {isRtl
                  ? "لا توجد نتائج تطابق بحثك"
                  : "No results found for your search"}
              </Typography>
            </Box>
          )}
        </Grid>
      </Container>

      {/* 3. Order Confirmation Dialog */}
      <Dialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 1,
            maxWidth: 500,
            width: "100%",
            mx: 2,
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={() => setOrderDialogOpen(false)}
          sx={{ position: "absolute", right: 15, top: 15, zIndex: 1 }}
        >
          <Close />
        </IconButton>

        <Box sx={{ textAlign: "center", p: 3 }}>
          <Box
            sx={{
              width: 130,
              height: 130,
              mx: "auto",
              mb: 2,
              p: 0.5,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${primaryMain}, ${alpha(primaryMain, 0.2)})`,
              boxShadow: `0 10px 20px ${alpha(primaryMain, 0.2)}`,
            }}
          >
            <Avatar
              src={selectedProduct ? joinUrl(selectedProduct.image) : ""}
              sx={{
                width: "100%",
                height: "100%",
                bgcolor: "background.paper",
              }}
            />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 1000, mb: 1 }}>
            {isRtl ? selectedProduct?.title_ar : selectedProduct?.title_en}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            {t("home.products.confirm_msg")}
          </Typography>

          {/* Quantity Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              my: 4,
              mx: "auto",
              width: "fit-content",
              bgcolor: alpha(primaryMain, 0.05),
              borderRadius: 12,
              p: 0.8,
              border: `1px solid ${alpha(primaryMain, 0.1)}`,
            }}
          >
            <Button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              sx={{
                minWidth: 50,
                height: 50,
                borderRadius: "50%",
                fontSize: 24,
                fontWeight: 900,
                color: primaryMain,
              }}
            >
              -
            </Button>

            <Typography
              variant="h4"
              sx={{ mx: 4, fontWeight: 1000, minWidth: 40 }}
            >
              {new Intl.NumberFormat(i18n.language).format(quantity)}
            </Typography>

            <Button
              onClick={() => setQuantity(quantity + 1)}
              sx={{
                minWidth: 50,
                height: 50,
                borderRadius: "50%",
                fontSize: 24,
                fontWeight: 900,
                bgcolor: primaryMain,
                color: "#fff",
                "&:hover": { bgcolor: primaryMain, transform: "scale(1.05)" },
              }}
            >
              +
            </Button>
          </Box>

          {/* Price Summary */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mb: 4,
              p: 2.5,
              borderRadius: 5,
              bgcolor: alpha(primaryMain, 0.03),
              border: `1px dashed ${alpha(primaryMain, 0.3)}`,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={800}
              color="text.secondary"
            >
              {t("home.products.total")}
            </Typography>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 1000 }}>
              {new Intl.NumberFormat(i18n.language).format(
                selectedProduct ? selectedProduct.price * quantity : 0,
              )}
              <Typography
                component="span"
                variant="caption"
                sx={{ ml: 0.8, fontWeight: 900 }}
              >
                EGP
              </Typography>
            </Typography>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              variant="text"
              onClick={() => setOrderDialogOpen(false)}
              sx={{ fontWeight: 900, color: "text.secondary", py: 1.8 }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={confirmOrder}
              sx={{
                borderRadius: 4,
                py: 1.8,
                fontWeight: 900,
                boxShadow: `0 8px 25px ${alpha(primaryMain, 0.4)}`,
              }}
            >
              {t("home.products.confirm_order")}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
